import { Card, Text, View } from "@src/components";
import type { RoadSenseTrafficSummaryCard } from "@visioni/a3s-reports/client-messages";

interface TrafficSummaryProps {
  cards: readonly RoadSenseTrafficSummaryCard[];
}

/**
 * "Traffic summary" block — a row of summary cards driven by the payload's
 * ordered `traffic_summary` list. Unlike WasteSense's fixed three-metric grid,
 * RoadSense's headline metrics vary per customer (a directional split for one
 * site, entries/peak for another), so this renders whatever ordered list the
 * generator emits.
 *
 * RoadSense-owned: a sibling of, not a fork of, `WeeklySummary`.
 */
export function TrafficSummary({ cards }: TrafficSummaryProps) {
  if (cards.length === 0) return null;

  return (
    <View gap="md">
      <Text textStyle="md/semibold" color="text">
        Traffic summary
      </Text>
      <View flexDirection="row" flexWrap="wrap" gap="md">
        {cards.map((card) => (
          <KpiCard key={card.id} card={card} />
        ))}
      </View>
    </View>
  );
}

function KpiCard({ card }: { card: RoadSenseTrafficSummaryCard }) {
  return (
    <Card flex={1} gap="xs" minWidth={160}>
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {card.label}
      </Text>
      <Text textStyle="h2" color="text">
        {card.value}
      </Text>
      {card.sub_label ?
        <Text textStyle="xs/regular" color="textMuted">
          {card.sub_label}
        </Text>
      : null}
      {card.delta ?
        <DeltaPill direction={card.delta.direction} label={card.delta.label} />
      : null}
    </Card>
  );
}

interface DeltaPillProps {
  direction: "up" | "down" | "flat";
  label: string;
}

/**
 * Period-over-period change indicator. We deliberately do **not** colour a
 * downward change red: "down" is not inherently bad for a traffic count, so a
 * down/flat delta reads in muted text while an upward delta gets the positive
 * (success) accent. The arrow glyph carries the direction for users who don't
 * perceive the colour difference.
 */
function DeltaPill({ direction, label }: DeltaPillProps) {
  const glyph =
    direction === "up" ? "▲"
    : direction === "down" ? "▼"
    : "—";
  const color = direction === "up" ? "success" : "textMuted";
  return (
    <View flexDirection="row" alignItems="center" gap="xxs">
      <Text textStyle="xs/semibold" color={color}>
        {glyph}
      </Text>
      <Text textStyle="xs/regular" color={color}>
        {label}
      </Text>
    </View>
  );
}
